import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    min: Number,
    max: Number,
    step: { type: Number, default: 1 },
    dragThreshold: { type: Number, default: 6 },
    fadeDuration: { type: Number, default: 160 }
  }

  connect () {
    this.dragging = false
    this.pointerId = null
    this.startX = 0
    this.hideTimer = null
    this.cancelNextClick = false
    this.build()
    this.bind()
    this.commitInput()
    this.hideImmediately()
    this.syncLayout()

    this.resizeObserver = new ResizeObserver(() => this.syncLayout())
    this.resizeObserver.observe(this.element)

    this.mutationObserver = new MutationObserver(() => this.syncLayout())
    this.mutationObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ['class', 'style']
    })
  }

  disconnect () {
    this.unbind()
    this.removeWindowListeners()
    clearTimeout(this.hideTimer)
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
  }

  build () {
    if (this.element.dataset.dragNumberBuilt === 'true') {
      this.input = this.element.querySelector('.drag-number__input')
      this.baseLayer = this.element.querySelector('.drag-number__layer--base')
      this.fillLayer = this.element.querySelector('.drag-number__fill')
      this.overlayLayer = this.element.querySelector('.drag-number__layer--fill')
      this.display = this.element.querySelector('.drag-number__value-display')
      return
    }

    const input = this.element.querySelector('input')
    if (!input) return

    this.element.dataset.dragNumberBuilt = 'true'
    this.element.classList.add('drag-number')

    const originalNodes = Array.from(this.element.childNodes)

    const baseLayer = document.createElement('span')
    baseLayer.className = 'drag-number__layer drag-number__layer--base'

    for (const node of originalNodes) {
      baseLayer.appendChild(node)
    }

    const liveInput = baseLayer.querySelector('input')
    liveInput.classList.add('drag-number__input')

    const fillLayer = document.createElement('span')
    fillLayer.className = 'drag-number__fill'

    const overlayLayer = baseLayer.cloneNode(true)
    overlayLayer.className = 'drag-number__layer drag-number__layer--fill'
    overlayLayer.setAttribute('aria-hidden', 'true')

    const overlayInput = overlayLayer.querySelector('input')
    const display = document.createElement('span')
    display.className = 'drag-number__value-display'
    display.textContent = liveInput.value || ''

    if (overlayInput) {
      overlayInput.replaceWith(display)
    } else {
      overlayLayer.appendChild(display)
    }

    this.element.append(fillLayer, baseLayer, overlayLayer)

    this.input = liveInput
    this.baseLayer = baseLayer
    this.fillLayer = fillLayer
    this.overlayLayer = overlayLayer
    this.display = display
  }

  bind () {
    if (!this.input) return

    this.onPointerDownBound = this.onPointerDown.bind(this)
    this.onPointerMoveBound = this.onPointerMove.bind(this)
    this.onPointerUpBound = this.onPointerUp.bind(this)
    this.onInputBound = this.onInput.bind(this)
    this.onChangeBound = this.onChange.bind(this)
    this.onClickCaptureBound = this.onClickCapture.bind(this)

    this.element.addEventListener('pointerdown', this.onPointerDownBound)
    this.element.addEventListener('click', this.onClickCaptureBound, true)
    this.input.addEventListener('input', this.onInputBound)
    this.input.addEventListener('change', this.onChangeBound)
    this.input.addEventListener('blur', this.onChangeBound)
  }

  unbind () {
    if (!this.input) return

    this.element.removeEventListener('pointerdown', this.onPointerDownBound)
    this.element.removeEventListener('click', this.onClickCaptureBound, true)
    this.input.removeEventListener('input', this.onInputBound)
    this.input.removeEventListener('change', this.onChangeBound)
    this.input.removeEventListener('blur', this.onChangeBound)
  }

  onPointerDown (event) {
    if (!this.input) return
    if (event.button !== undefined && event.button !== 0) return

    this.dragging = false
    this.pointerId = event.pointerId
    this.startX = event.clientX

    window.addEventListener('pointermove', this.onPointerMoveBound)
    window.addEventListener('pointerup', this.onPointerUpBound)
    window.addEventListener('pointercancel', this.onPointerUpBound)
  }

  onPointerMove (event) {
    if (this.pointerId !== event.pointerId) return

    const deltaX = event.clientX - this.startX

    if (!this.dragging) {
      if (Math.abs(deltaX) < this.dragThresholdValue) return

      this.dragging = true
      this.cancelNextClick = true
      this.element.classList.add('is-dragging')
      this.input.blur()
      this.input.setPointerCapture?.(event.pointerId)
      this.show()
    }

    event.preventDefault()

    const nextValue = this.valueFromPointer(event.clientX)
    this.setValue(nextValue, true)
    this.show()
  }

  onPointerUp (event) {
    if (this.pointerId !== event.pointerId) return

    this.removeWindowListeners()

    if (this.dragging) {
      this.dragging = false
      this.element.classList.remove('is-dragging')

      this.input.dispatchEvent(new Event('change', { bubbles: true }))
      this.hideSoon()
    } else {
      this.hideImmediately()
      this.input.focus()
    }

    this.pointerId = null
  }

  onClickCapture (event) {
    if (!this.cancelNextClick) return

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    this.cancelNextClick = false
  }

  onInput () {
    const value = this.numericValue(this.input.value, null)
    if (value === null) return
    this.updateVisuals(this.clamp(value))
  }

  onChange () {
    this.commitInput()
  }

  commitInput () {
    const value = this.snap(this.clamp(this.numericValue(this.input.value, this.min())))
    this.input.value = this.format(value)
    this.updateVisuals(value)
  }

  setValue (value, emitInput = false) {
    this.input.value = this.format(value)
    this.updateVisuals(value)

    if (emitInput) {
      this.input.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  valueFromPointer (clientX) {
    const rect = this.element.getBoundingClientRect()
    const width = Math.max(rect.width, 1)

    const xAxisValue = this.clampBetween(clientX - rect.left, 0, width)
    const percent = xAxisValue / width

    const rawValue = this.min() + percent * (this.max() - this.min())
    return this.snap(this.clamp(rawValue))
  }

  updateVisuals (value) {
    const min = this.min()
    const max = this.max()
    const range = Math.max(max - min, 1)
    const ratio = (value - min) / range
    const percent = Math.max(0, Math.min(100, ratio * 100))

    this.element.style.setProperty('--drag-number-fill-width', `${percent}%`)

    if (this.display) {
      this.display.textContent = this.format(value)
    }
  }

  show () {
    clearTimeout(this.hideTimer)
    this.element.classList.add('is-visible')
  }

  hideSoon () {
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => {
      if (!this.dragging) {
        this.element.classList.remove('is-visible')
      }
    }, this.fadeDurationValue)
  }

  hideImmediately () {
    clearTimeout(this.hideTimer)
    this.element.classList.remove('is-visible')
  }

  syncLayout () {
    if (!this.baseLayer || !this.overlayLayer) return

    const styles = getComputedStyle(this.element)

    const layoutProps = [
      'display',
      'flex-direction',
      'flex-wrap',
      'align-items',
      'justify-content',
      'align-content',
      'justify-items',
      'place-items',
      'place-content',
      'gap',
      'row-gap',
      'column-gap',
      'grid-template-columns',
      'grid-template-rows',
      'grid-auto-flow',
      'grid-auto-columns',
      'grid-auto-rows',
      'text-align'
    ]

    for (const prop of layoutProps) {
      const value = styles.getPropertyValue(prop)
      this.baseLayer.style.setProperty(prop, value)
      this.overlayLayer.style.setProperty(prop, value)
    }

    this.baseLayer.style.width = '100%'
    this.baseLayer.style.height = '100%'
    this.baseLayer.style.minWidth = '0'
    this.baseLayer.style.boxSizing = 'border-box'
    this.baseLayer.style.position = 'relative'

    this.overlayLayer.style.width = '100%'
    this.overlayLayer.style.height = '100%'
    this.overlayLayer.style.minWidth = '0'
    this.overlayLayer.style.boxSizing = 'border-box'
    this.overlayLayer.style.position = 'absolute'
    this.overlayLayer.style.inset = '0'
    this.overlayLayer.style.pointerEvents = 'none'

    this.overlayLayer.style.paddingTop = styles.paddingTop
    this.overlayLayer.style.paddingRight = styles.paddingRight
    this.overlayLayer.style.paddingBottom = styles.paddingBottom
    this.overlayLayer.style.paddingLeft = styles.paddingLeft
    this.overlayLayer.style.borderRadius = styles.borderRadius
  }

  removeWindowListeners () {
    window.removeEventListener('pointermove', this.onPointerMoveBound)
    window.removeEventListener('pointerup', this.onPointerUpBound)
    window.removeEventListener('pointercancel', this.onPointerUpBound)
  }

  numericValue (value, fallback) {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  clampBetween (value, min, max) {
    return Math.min(max, Math.max(min, value))
  }

  min () {
    if (this.hasMinValue) return this.minValue
    const attr = parseFloat(this.input?.min)
    return Number.isFinite(attr) ? attr : 0
  }

  max () {
    if (this.hasMaxValue) return this.maxValue
    const attr = parseFloat(this.input?.max)
    return Number.isFinite(attr) ? attr : 100
  }

  clamp (value) {
    return Math.min(this.max(), Math.max(this.min(), value))
  }

  snap (value) {
    const step = this.stepValue || 1
    const min = this.min()
    const snapped = Math.round((value - min) / step) * step + min
    return this.clamp(snapped)
  }

  format (value) {
    const step = this.stepValue || 1

    if (step >= 1) return `${Math.round(value)}`

    const decimals = `${step}`.includes('.') ? `${step}`.split('.')[1].length : 0

    return value.toFixed(decimals)
  }
}
